export const extractErrorMessage = (
  error: unknown,
  fallbackMessage: string = 'An error occurred. Please try again.'
): string => {
  if (!error) {
    return fallbackMessage;
  }

  try {
    const errorData = JSON.parse((error as Error).message);
    if (errorData?.non_field_errors) {
      return errorData?.non_field_errors[0].message || fallbackMessage;
    }
    return errorData?.message || errorData?.detail || fallbackMessage;
  } catch {
    return (error as Error).message || fallbackMessage;
  }
};

type RetryOptions = {
  retries: number;
  delayMs: number;
  onRetry?: (err: Error, attempt: number) => void;
  onMaxReached?: (err: Error) => void;
  shouldRetry?: (err: Error) => boolean;
  jitterMs?: number;
};

export async function asyncRetry<T>(fn: () => Promise<T>, options: RetryOptions): Promise<T> {
  const { retries, delayMs, onRetry, onMaxReached } = options;
  let lastError: Error | undefined;

  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastError = err as Error;
      onRetry?.(lastError, attempt);

      if (attempt < retries) {
        await new Promise(resolve => setTimeout(resolve, delayMs));
      }
    }
  }

  onMaxReached?.(lastError!);
  throw lastError || new Error('Max retries reached');
}

export function retry<T>(fn: () => T, options: Omit<RetryOptions, 'delayMs'>): T {
  const { retries, onRetry, onMaxReached } = options;
  let lastError: Error | undefined;

  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      return fn();
    } catch (err) {
      lastError = err as Error;
      onRetry?.(lastError, attempt);
    }
  }

  onMaxReached?.(lastError!);
  throw lastError || new Error('Max retries reached');
}

export async function exponentialRetry<T>(fn: () => Promise<T>, options: RetryOptions): Promise<T> {
  const { retries, delayMs, onRetry, onMaxReached, shouldRetry, jitterMs = 0 } = options;
  let lastError: Error | undefined;

  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastError = err as Error;
      if (shouldRetry && !shouldRetry(lastError)) {
        throw lastError;
      }
      onRetry?.(lastError, attempt);
      if (attempt < retries) {
        const delay = delayMs * Math.pow(2, attempt) + Math.random() * jitterMs;
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  onMaxReached?.(lastError!);
  throw lastError || new Error('Max retries reached');
}

export function getSpeakerRank(broadcastData: any, userId: string): number | undefined {
  if (!broadcastData) {
    return undefined;
  }

  const speaker = broadcastData.speakers?.find((speaker: any) => speaker.id === userId);
  if (speaker?.stage_rank != null) {
    return speaker.stage_rank;
  }

  const staff = (broadcastData.staff_details || []).find((staff: any) => staff.id === userId);
  return staff?.stage_rank;
}
